import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './JobHistory.css';
import userService from '../../services/userService';
import pipelineService from '../../services/pipelineService';
import twodService from '../../services/twodService';
import { formatTimeWithTimezone } from '../../utils/timeUtils';
import { getDisplayFilename, formatFileSize } from '../../utils/fileUtils';
import { errorLog } from '../../utils/logger';
import parameterLabels from '../../config/parameterLabels.json';
import MeshPreview from '../preview/MeshPreview';
import SecureImage from '../preview/SecureImage';
import { useNavigate } from 'react-router-dom';

const JobHistory = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobFiles, setJobFiles] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState('files'); // 'files' | 'preview'
  const [mountMeshPreview, setMountMeshPreview] = useState(false);
  const [twodExports, setTwodExports] = useState([]);
  const [loadingTwod, setLoadingTwod] = useState(true);
  const [twodError, setTwodError] = useState('');
  const [activeHistoryView, setActiveHistoryView] = useState('pipeline'); // 'pipeline' | 'twod'
  const [reworkingSessionId, setReworkingSessionId] = useState(null);

  const pageSize = 10;
  const totalPages = Math.ceil(totalJobs / pageSize);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await userService.getJobHistory({
        page: currentPage,
        pageSize,
        statusFilter: statusFilter || null,
      });
      
      setJobs(response.jobs || []);
      setTotalJobs(response.total || 0);
    } catch (err) {
      setError('Failed to load job history. Please try again.');
      errorLog('Error fetching job history:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const fetchTwodExports = useCallback(async () => {
    try {
      setLoadingTwod(true);
      setTwodError('');

      const response = await twodService.listUserExports();
      const jobsMap = response.jobs || {};

      const exportsList = Object.values(jobsMap).map((entry) => {
        const info = entry.job_info || {};
        return {
          jobId: info.job_id,
          displayName: info.display_name || info.job_id?.slice(0, 8) || '2D export',
          createdAt: info.created_at,
          completedAt: info.completed_at,
          fileCount: entry.file_count || 0,
        };
      });

      exportsList.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });

      setTwodExports(exportsList);
    } catch (err) {
      setTwodError('Failed to load 2D exports. Please try again.');
      errorLog('Error fetching 2D exports:', err);
    } finally {
      setLoadingTwod(false);
    }
  }, []);

  useEffect(() => {
    fetchTwodExports();
  }, [fetchTwodExports]);

  const handleJobSelect = async (job) => {
    setSelectedJob(job);
    setJobFiles(null);
    setLoadingFiles(true);
    setActiveModalTab('files');
    setMountMeshPreview(false);

    try {
      const filesResponse = await pipelineService.listFiles(job.job_uuid);
      setJobFiles(filesResponse);
    } catch (err) {
      errorLog('Error fetching job files:', err);
      setJobFiles({ error: 'Failed to load files for this job.' });
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleCloseJobDetails = () => {
    setSelectedJob(null);
    setJobFiles(null);
    setMountMeshPreview(false);
  };

  useEffect(() => {
    if (activeModalTab !== 'preview' && mountMeshPreview) {
      // Unmount mesh preview when leaving the preview tab to free resources
      setMountMeshPreview(false);
    }
  }, [activeModalTab, mountMeshPreview]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-badge status-badge--completed';
      case 'processing':
        return 'status-badge status-badge--processing';
      case 'failed':
      case 'cancelled':
        return 'status-badge status-badge--failed';
      case 'pending':
        return 'status-badge status-badge--pending';
      default:
        return 'status-badge';
    }
  };

  const canReworkJob = (status) => ['completed', 'failed', 'cancelled'].includes(String(status || '').toLowerCase());

  const formatAlgorithm = (algorithm) => {
    if (!algorithm) return 'Unknown';
    const normalized = algorithm.replace(/^pipeline_/, '');
    const key = `mesh_generation.algorithm.option.${normalized}`;
    const override = parameterLabels[key]?.label;
    if (override) {
      return override;
    }
    return normalized
      .split('_')
      .map((segment) => (segment ? segment[0].toUpperCase() + segment.slice(1) : segment))
      .join(' ');
  };

  const isPrimaryAsset = (filename = '') => {
    const lower = (filename || '').toLowerCase();
    if (!lower) return false;
    if (lower.includes('preview')) {
      return false;
    }
    if ((lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) && lower.includes('original')) {
      return true;
    }
    if (lower.endsWith('.obj') || lower.endsWith('.mtl') || lower.endsWith('.ply')) {
      return true;
    }
    if (
      (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) &&
      (lower.includes('texture') || lower.includes('material'))
    ) {
      return true;
    }
    return false;
  };

  const handleDownloadFile = async (file) => {
    if (!selectedJob || !file?.stepType || !file?.filename) return;
    
    try {
      const blob = await pipelineService.downloadFile(selectedJob.job_uuid, file.stepType, file);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.filename.split('/').pop() || file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      errorLog('Error downloading file:', error);
      
      if (error.response?.status === 401) {
        alert('Your session has expired. Please refresh the page and log in again.');
      } else {
        alert('Failed to download file. Please try again.');
      }
    }
  };

  const handleDownloadTwodExport = async (jobId, displayName) => {
    if (!jobId) {
      return;
    }

    try {
      const blob = await twodService.downloadExport(jobId);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = displayName || `${jobId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      errorLog('Error downloading 2D export:', error);
      alert('Failed to download 2D export. Please try again.');
    }
  };

  const handleReworkJob = async (jobId, event) => {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    if (!jobId || reworkingSessionId) {
      return;
    }

    try {
      setReworkingSessionId(jobId);
      const response = await pipelineService.reworkSession(jobId);
      navigate('/image-processing', {
        state: {
          reworkSessionId: response.session_id,
          reworkSourceSessionId: response.source_session_id || jobId,
          reworkAlgorithm: response.algorithm || null,
          reworkStepParameters: response.step_parameters || {},
          reworkTextureSettings: response.texture_settings || null,
          reworkOriginalImageFilename: response.original_image?.filename || null,
          reworkMessage: response.message || '',
          reworkMaskAvailable: response.mask_available === true,
        },
      });
    } catch (error) {
      errorLog('Error preparing rework session:', error);
      alert('Failed to prepare rework session. Please try again.');
    } finally {
      setReworkingSessionId(null);
    }
  };

  // Derive previewable assets lazily from fetched file listing
  const previewAssets = useMemo(() => {
    if (!selectedJob || !jobFiles?.files_by_step) {
      return { objUrl: null, objSize: 0, textureUrl: null, originals: [], depthImages: [] };
    }

    const byStep = jobFiles.files_by_step;
    const pickFiles = (stepName) => {
      const entry = Object.values(byStep).find((v) => v?.step_type === stepName);
      if (!entry) return [];
      const consolidated = Array.isArray(entry.consolidated_files) ? entry.consolidated_files : [];
      const fallback = Array.isArray(entry.fallback_files) ? entry.fallback_files : [];
      return [...consolidated, ...fallback].map((f) => ({ ...f, stepType: entry.step_type }));
    };

    const meshFiles = pickFiles('mesh_generation');
    const backgroundFiles = pickFiles('background_removal');
    const depthFiles = pickFiles('depth_generation');

    const firstObj =
      meshFiles.find((f) => typeof f.filename === 'string' && /mesh_preview\.obj$/i.test(f.filename)) ||
      meshFiles.find((f) => typeof f.filename === 'string' && f.filename.toLowerCase().endsWith('.obj'));
    const firstTexture = meshFiles.find((f) => {
      const name = (f.filename || '').toLowerCase();
      return name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
    });

    const objUrl = firstObj
      ? pipelineService.resolveFilePreviewUrl(selectedJob.job_uuid, firstObj.stepType, firstObj)
      : null;
    const textureUrl = firstTexture
      ? pipelineService.resolveFilePreviewUrl(selectedJob.job_uuid, firstTexture.stepType, firstTexture)
      : null;
    const objSize = Number(firstObj?.file_size || firstObj?.size || 0) || 0;

    // Images for quick visual context
    const originals = backgroundFiles.filter((f) => /original/i.test(f.filename) && /\.(png|jpg|jpeg)$/i.test(f.filename))
      .slice(0, 1)
      .map((f) => ({
        url: pipelineService.resolveFilePreviewUrl(selectedJob.job_uuid, f.stepType, f),
        name: f.filename,
      }));
    const depthImages = depthFiles.filter((f) => /\.(png|jpg|jpeg)$/i.test(f.filename))
      .slice(0, 1)
      .map((f) => ({
        url: pipelineService.resolveFilePreviewUrl(selectedJob.job_uuid, f.stepType, f),
        name: f.filename,
      }));

    return { objUrl, objSize, textureUrl, originals, depthImages };
  }, [selectedJob, jobFiles]);

  const isHeavyObj = useMemo(() => previewAssets.objSize > 25 * 1024 * 1024, [previewAssets.objSize]);

  if (loading && jobs.length === 0) {
    return (
      <div className="job-history">
        <div className="job-history__loading">
          <div className="loading-spinner"></div>
          <p>Loading job history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-history">
      <div className="job-history__header">
        <h2>Processing History</h2>
        <div className="job-history__header-right">
          <div className="job-history__tabs">
            <button
              type="button"
              className={`job-history__tab ${activeHistoryView === 'pipeline' ? 'job-history__tab--active' : ''}`}
              onClick={() => setActiveHistoryView('pipeline')}
            >
              3D Pipeline Jobs
            </button>
            <button
              type="button"
              className={`job-history__tab ${activeHistoryView === 'twod' ? 'job-history__tab--active' : ''}`}
              onClick={() => setActiveHistoryView('twod')}
            >
              2D Image Exports
            </button>
          </div>
          {activeHistoryView === 'pipeline' && (
            <div className="job-history__filters">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="status-filter"
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {activeHistoryView === 'pipeline' && error && (
        <div className="job-history__error">
          <p>{error}</p>
          <button onClick={fetchJobs} className="btn-secondary">
            Retry
          </button>
        </div>
      )}

      {activeHistoryView === 'pipeline' && jobs.length === 0 && !loading && !error && (
        <div className="job-history__empty">
          <p>No processing jobs found. Start by uploading an image!</p>
        </div>
      )}

      {activeHistoryView === 'pipeline' && jobs.length > 0 && (
        <>
          <div className="job-history__list">
            {jobs.map((job) => (
              <div
                key={job.job_uuid}
                className={`job-card ${selectedJob?.job_uuid === job.job_uuid ? 'job-card--selected' : ''}`}
                onClick={() => handleJobSelect(job)}
              >
                <div className="job-card__header">
                  <div className="job-card__info">
                    <h3 className="job-card__algorithm">{formatAlgorithm(job.algorithm)}</h3>
                    <p className="job-card__id">ID: {job.job_uuid.slice(0, 8)}...</p>
                  </div>
                  <span className={getStatusBadgeClass(job.status)}>
                    {job.status}
                  </span>
                </div>
                
                <div className="job-card__details">
                  <div className="job-card__meta">
                    <span>Created: {formatTimeWithTimezone(job.created_at)}</span>
                    {job.completed_at && (
                      <span>Completed: {formatTimeWithTimezone(job.completed_at)}</span>
                    )}
                  </div>
                  
                  {job.status === 'processing' && (
                    <div className="job-card__progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-bar__fill"
                          style={{ width: `${job.progress_percentage || 0}%` }}
                        />
                      </div>
                      <span className="progress-text">
                        {job.completed_steps || 0} / {job.total_steps || 0} steps
                      </span>
                    </div>
                  )}
                </div>

                {canReworkJob(job.status) ? (
                  <div className="job-card__actions">
                    <button
                      type="button"
                      className="job-card__rework"
                      disabled={reworkingSessionId === job.job_uuid}
                      onClick={(event) => handleReworkJob(job.job_uuid, event)}
                    >
                      {reworkingSessionId === job.job_uuid ? 'Preparing…' : 'Rework'}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="job-history__pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({totalJobs} total jobs)
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {activeHistoryView === 'twod' && (
        <div className="job-history__twod-section">
        <div className="job-history__twod-header">
          <h3>2D Image Exports</h3>
        </div>

        {twodError && (
          <div className="job-history__error">
            <p>{twodError}</p>
            <button onClick={fetchTwodExports} className="btn-secondary">
              Retry
            </button>
          </div>
        )}

        {!twodError && loadingTwod && (
          <div className="job-history__empty">
            <p>Loading 2D exports...</p>
          </div>
        )}

        {!twodError && !loadingTwod && twodExports.length === 0 && (
          <div className="job-history__empty">
            <p>No 2D exports saved yet.</p>
          </div>
        )}

        {!twodError && !loadingTwod && twodExports.length > 0 && (
          <div className="job-history__twod-list">
            {twodExports.map((exp) => {
              const url = twodService.buildDownloadUrl(exp.jobId);
              return (
                <div key={exp.jobId} className="twod-export-card">
                  <div className="twod-export-card__thumb">
                    {url && (
                      <SecureImage
                        src={url}
                        alt={exp.displayName}
                        className="twod-export-card__img"
                      />
                    )}
                  </div>
                  <div className="twod-export-card__info">
                    <span className="twod-export-card__name">{exp.displayName}</span>
                    {exp.createdAt && (
                      <span className="twod-export-card__meta">
                        Saved: {formatTimeWithTimezone(exp.createdAt)}
                      </span>
                    )}
                    {exp.fileCount ? (
                      <span className="twod-export-card__meta">
                        Files: {exp.fileCount}
                      </span>
                    ) : null}
                  </div>
                  {url && (
                    <button
                      type="button"
                      onClick={() => handleDownloadTwodExport(exp.jobId, exp.displayName)}
                      className="twod-export-card__download"
                    >
                      Download
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {selectedJob && (
        <div className="job-details-modal">
          <div className="job-details-modal__backdrop" onClick={handleCloseJobDetails} />
          <div className="job-details-modal__content">
            <div className="job-details__header">
              <h3>Job Details: {formatAlgorithm(selectedJob.algorithm)}</h3>
              <div className="job-details__header-actions">
                {canReworkJob(selectedJob.status) ? (
                  <button
                    type="button"
                    className="job-details__rework"
                    disabled={reworkingSessionId === selectedJob.job_uuid}
                    onClick={(event) => handleReworkJob(selectedJob.job_uuid, event)}
                  >
                    {reworkingSessionId === selectedJob.job_uuid ? 'Preparing…' : 'Rework'}
                  </button>
                ) : null}
                <button
                  onClick={handleCloseJobDetails}
                  className="job-details__close"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="job-details__info">
              <div className="job-details__meta">
                <p><strong>Job ID:</strong> {selectedJob.job_uuid}</p>
                <p><strong>Status:</strong> <span className={getStatusBadgeClass(selectedJob.status)}>{selectedJob.status}</span></p>
                <p><strong>Algorithm:</strong> {formatAlgorithm(selectedJob.algorithm)}</p>
                <p><strong>Created:</strong> {formatTimeWithTimezone(selectedJob.created_at)}</p>
                {selectedJob.completed_at && (
                  <p><strong>Completed:</strong> {formatTimeWithTimezone(selectedJob.completed_at)}</p>
                )}
              </div>
            </div>

            {selectedJob.status === 'completed' && (
              <div className="job-details__tabs">
                <button
                  type="button"
                  className={`job-details__tab ${activeModalTab === 'files' ? 'job-details__tab--active' : ''}`}
                  onClick={() => setActiveModalTab('files')}
                >
                  Files
                </button>
                <button
                  type="button"
                  className={`job-details__tab ${activeModalTab === 'preview' ? 'job-details__tab--active' : ''}`}
                  onClick={() => setActiveModalTab('preview')}
                >
                  Preview
                </button>
              </div>
            )}

            {loadingFiles && (
              <div className="job-details__loading">
                <div className="loading-spinner"></div>
                <p>Loading files...</p>
              </div>
            )}

                {jobFiles && !loadingFiles && activeModalTab === 'files' && (
                  <div className="job-details__files">
                    <div className="job-details__files-header">
                      <h4>Available Files</h4>
                    </div>

                    {jobFiles.error ? (
                      <p className="job-details__error">{jobFiles.error}</p>
                    ) : (
                      <div className="job-files-list">
                        {(() => {
                          const filesByStep = jobFiles.files_by_step || {};
                          const candidates = [];

                          Object.values(filesByStep).forEach((stepData) => {
                            const stepType = stepData?.step_type;
                            const consolidated = Array.isArray(stepData?.consolidated_files)
                              ? stepData.consolidated_files
                              : [];
                            const fallback = Array.isArray(stepData?.fallback_files)
                              ? stepData.fallback_files
                              : [];
                            [...consolidated, ...fallback].forEach((file) => {
                              if (!file || !isPrimaryAsset(file.filename)) {
                                return;
                              }
                              candidates.push({
                                ...file,
                                stepType,
                              });
                            });
                          });

                          const uniqueMap = new Map();
                          const shouldReplace = (existing, candidate) => {
                            const existingSize = existing.file_size ?? existing.size ?? 0;
                            const candidateSize = candidate.file_size ?? candidate.size ?? 0;
                            return candidateSize > existingSize;
                          };

                          candidates.forEach((file) => {
                            const key = `${file.stepType || ''}|${(file.filename || '').toLowerCase()}`;
                            const existing = uniqueMap.get(key);
                            if (!existing || shouldReplace(existing, file)) {
                              uniqueMap.set(key, file);
                            }
                          });

                          const prioritized = Array.from(uniqueMap.values()).sort((a, b) => {
                            const nameA = (a.filename || '').toLowerCase();
                            const nameB = (b.filename || '').toLowerCase();
                            const weight = (name) => {
                              if (name.endsWith('.obj')) return 100;
                              if (name.endsWith('.mtl')) return 90;
                              if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg')) {
                                if (name.includes('texture') || name.includes('material')) return 80;
                              }
                              if (name.endsWith('.ply')) return 70;
                              return 0;
                            };
                            const wa = weight(nameA);
                            const wb = weight(nameB);
                            if (wa !== wb) return wb - wa;
                            return nameA.localeCompare(nameB);
                          });

                          return prioritized.map((file, index) => {
                            const displayName = getDisplayFilename(file.filename, file.stepType);
                            const sizeLabel = formatFileSize(file.file_size ?? file.size);

                            return (
                              <div key={index} className="job-file">
                                <div className="job-file__details">
                                  <span className="job-file__name">{displayName}</span>
                                  <span className="job-file__meta">{sizeLabel}</span>
                                </div>
                                <button
                                  className="job-file__download"
                                  onClick={() => handleDownloadFile(file)}
                                >
                                  Download
                                </button>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                )}

            {jobFiles && !loadingFiles && activeModalTab === 'preview' && (
              <div className="job-details__preview">
                {!previewAssets.objUrl && previewAssets.originals.length === 0 && previewAssets.depthImages.length === 0 ? (
                  <p className="job-details__error">No preview assets available for this job.</p>
                ) : (
                  <>
                    {/* Light image previews to avoid heavy loads */}
                    <div className="job-preview__images">
                      {previewAssets.originals.map((img, idx) => (
                        <div key={`orig-${idx}`} className="job-preview__image">
                          <SecureImage src={img.url} alt={img.name} className="job-preview__img-el" />
                          <span className="job-preview__caption">Original</span>
                        </div>
                      ))}
                      {previewAssets.depthImages.map((img, idx) => (
                        <div key={`depth-${idx}`} className="job-preview__image">
                          <SecureImage src={img.url} alt={img.name} className="job-preview__img-el" />
                          <span className="job-preview__caption">Depth</span>
                        </div>
                      ))}
                    </div>

                    {/* 3D mesh preview is opt-in to avoid heavy GPU/memory work */}
                    {previewAssets.objUrl ? (
                      <div className="job-preview__mesh">
                        {isHeavyObj ? (
                          <div className="job-details__error" style={{ marginBottom: 12 }}>
                            Mesh is large ({formatFileSize(previewAssets.objSize)}). Download is recommended for best performance.
                          </div>
                        ) : null}

                        {!mountMeshPreview ? (
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setMountMeshPreview(true)}
                          >
                            Load 3D Preview
                          </button>
                        ) : (
                          <MeshPreview objUrl={previewAssets.objUrl} textureUrl={previewAssets.textureUrl} />
                        )}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobHistory;
